import React from 'react';
import {defineMessage} from 'react-intl';
import {AccessControlIndicator} from '@igp/shared/access-control';
import {SortOrder} from '@igp/shared/api';
import {useAutoMapper} from '@igp/shared/auto-mapper';
import {ErrorModal} from '@igp/shared/components/error';
import {Toolbar, ToolbarItems, ToolbarItemsContainer} from '@igp/shared/components/layout';
import {withTraceErrorBoundary} from '@igp/shared/otel';
import type {ViewType} from '@igp/shared/redux/view';
import {getNonEmptyValueValidator} from '@igp/shared/redux/view';
import {momentToTimestampSeconds} from '@igp/shared/utils';

import {withAccessCheck} from '@access-control';
import {agentResources} from '@policy-access';
import {useAgentId} from '@features/account/hooks';
import type {AgentLevelReportV2ViewModelsKeys, DataGridAgentLevelReportV2Keys} from '@features/agent-level-report-v2';
import {
    AgentLevelReportV2DownloadCsvButton,
    AgentLevelReportV2DownloadViewModelsKeysTypeName,
    agentLevelReportV2MappingKeys,
    AgentLevelReportV2QueryFilters,
    DataGridAgentLevelReportV2,
    firstAvailableDate,
    useAgentLevelReportV2,
    useAgentLevelReportV2Settings,
} from '@features/agent-level-report-v2';

import {HomePageLayout, noPermissionLocalization} from './HomePageLayout';

const fileName = defineMessage({
    id: 'RevenueShareHistory_fileName',
    defaultMessage: 'Current agent revenue share history',
});

function RevenueShareHistoryInner() {
    const mapper = useAutoMapper();
    const viewType: ViewType = 'RevenueShareHistory';
    const pinnedColumn: DataGridAgentLevelReportV2Keys = 'weekStartDateTs';
    const columns: DataGridAgentLevelReportV2Keys[] = [
        'weekStartDateTs',
        'pokerNlheSdNetShare',
        'pokerMttNetShare',
        'pokerCashPloNetShare',
        'casinoNetShare',
        'globalSpinsNetShare',
        'sportsNetShare',
        'totalNetShare',
        'details',
    ];
    const fields: AgentLevelReportV2ViewModelsKeys[] = mapper.map<DataGridAgentLevelReportV2Keys[], AgentLevelReportV2ViewModelsKeys[]>(
        columns,
        agentLevelReportV2MappingKeys.ColumnKeys,
        agentLevelReportV2MappingKeys.ViewModelsKeys
    );
    const agentUid = useAgentId();
    const {
        items,
        rawItems,
        totalCount,
        searchFilter,
        filterString,
        handleFilterChange,
        handlePageChange,
        handlePageSizeChange,
        handleSortChange,
    } = useAgentLevelReportV2({
        viewType,
        fields,
        defaultPaging: {page: 1, pageSize: 25},
        defaultFilters: [
            {key: 'agent_uid', value: agentUid},
            {key: 'is_downstream', value: false},
            {key: 'type', value: 'weekly'},
        ],
        hiddenFilters: [{key: 'week_start_date_ts.min', value: momentToTimestampSeconds(firstAvailableDate)}],
        defaultSorting: [{field: 'weekStartDateTs', sort: SortOrder.Desc}],
        validateFilter: filter =>
            getNonEmptyValueValidator('agent_uid')(filter) && filter?.find(f => f.key === 'type')?.value?.toString() === 'weekly',
        syncWithUrl: true,
        blockFetchWithInvalidFilter: true,
    });
    const {visibleColumns, onVisibleColumnsChange} = useAgentLevelReportV2Settings({
        settingId: viewType,
        defaultColumns: columns,
        pinnedColumns: [pinnedColumn],
    });
    const downloadableColumnFields: AgentLevelReportV2ViewModelsKeys[] = mapper.map<
        DataGridAgentLevelReportV2Keys[],
        AgentLevelReportV2ViewModelsKeys[]
    >(visibleColumns, agentLevelReportV2MappingKeys.ColumnKeys, AgentLevelReportV2DownloadViewModelsKeysTypeName);
    return (
        <HomePageLayout
            toolbar={
                <Toolbar>
                    <ToolbarItemsContainer primary>
                        <ToolbarItems>
                            <AgentLevelReportV2QueryFilters
                                model={filterString}
                                onChange={handleFilterChange}
                                availableFilters={['weekStartDate']}
                            />
                        </ToolbarItems>
                        <ToolbarItems secondary>
                            <AgentLevelReportV2DownloadCsvButton
                                keys={downloadableColumnFields}
                                filter={filterString}
                                filename={fileName}
                                total={totalCount}
                                disabled={!totalCount}
                                mode="server"
                            />
                        </ToolbarItems>
                    </ToolbarItemsContainer>
                </Toolbar>
            }
            body={
                <DataGridAgentLevelReportV2
                    configurableColumns={{
                        pinnedColumns: [pinnedColumn],
                        visibleColumns,
                        onVisibleColumnsChange,
                    }}
                    columns={columns}
                    rows={items}
                    rawModels={rawItems}
                    paging={{
                        ...searchFilter?.paging,
                        rowCount: totalCount,
                    }}
                    onPageChange={handlePageChange}
                    onPageSizeChange={handlePageSizeChange}
                    sortModel={{
                        sortModel: searchFilter?.sorting,
                    }}
                    onSortChange={handleSortChange}
                />
            }
        />
    );
}

const RevenueShareHistoryWithAccess = withAccessCheck(
    RevenueShareHistoryInner,
    agentResources,
    AccessControlIndicator.InfoMessage,
    noPermissionLocalization
);

export const RevenueShareHistory = withTraceErrorBoundary(RevenueShareHistoryWithAccess, ErrorModal);
